import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { toast } from '../components/toasts';
import { Badge, Button, Empty, ErrorText, Spinner } from '../components/ui';
import { errorMessage, get, post } from '../lib/api';
import { dateTimeOf, SYNC_STATUS_LABEL, SYNC_TYPE_LABEL } from '../lib/format';
import { SyncTask } from '../lib/types';

/**
 * Pendências com o Agidesk (aguardando ação, falhas e em andamento). A supervisão (`all`) vê todas;
 * o operador, as dos atendimentos em que é o responsável (o servidor filtra).
 */
export function PendingSyncPage({ onOpen, all }: { onOpen: (attendanceId: string) => void; all: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['pending-sync'], queryFn: () => get<SyncTask[]>('/ticket-sync/tasks') });

  const retry = async (id: string) => {
    try {
      await post(`/ticket-sync/tasks/${id}/retry`);
      toast('Nova tentativa agendada', 'ok');
      void qc.invalidateQueries({ queryKey: ['pending-sync'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div className="mx-auto h-full max-w-5xl overflow-y-auto p-3 md:p-6">
      <h1 className="mb-1 text-lg font-semibold">{all ? 'Pendências com o Agidesk' : 'Minhas pendências com o Agidesk'}</h1>
      <p className="mb-4 text-sm text-fg-soft">
        {all ? 'Registros que ainda não chegaram ao Agidesk.' : 'Registros dos seus atendimentos que ainda não chegaram ao Agidesk.'} "Precisa de ação" pede correção (cliente,
        chamado, campos de encerramento) antes de tentar de novo: abra o atendimento e use "Definir" no painel do chamado quando ele foi finalizado com "decidir depois".
      </p>
      {isLoading && <Spinner />}
      <ErrorText error={error} />
      {data && data.length === 0 && <Empty>{all ? 'Nenhuma pendência.' : 'Nenhuma pendência nos seus atendimentos.'}</Empty>}
      {data && data.length > 0 && (
        <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Etapa</th>
              <th className="px-3 py-2">Contato / operador</th>
              <th className="px-3 py-2">Situação</th>
              <th className="px-3 py-2">Atualizado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.id} className="border-t border-border align-top">
                <td className="px-3 py-2">{SYNC_TYPE_LABEL[t.type] ?? t.type}</td>
                <td className="px-3 py-2">
                  <div>{t.contactName ?? '—'}</div>
                  <div className="text-xs text-fg-soft">{t.operatorName ?? ''}</div>
                </td>
                <td className="px-3 py-2">
                  <Badge tone={t.status === 'waiting' || t.status === 'failed' ? 'danger' : 'neutral'}>{SYNC_STATUS_LABEL[t.status] ?? t.status}</Badge>
                  {t.lastError && <p className="mt-1 max-w-sm text-xs text-danger">{t.lastError}</p>}
                  <p className="mt-0.5 text-xs text-fg-soft">{t.attempts} tentativa(s)</p>
                </td>
                <td className="px-3 py-2 text-xs text-fg-soft">{dateTimeOf(t.updatedAt)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => onOpen(t.attendanceId)}>
                    Abrir
                  </Button>
                  {t.status !== 'running' && (
                    <Button size="sm" onClick={() => void retry(t.id)}>
                      <RefreshCw className="size-3.5" /> Tentar de novo
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
